/**
 * pipeline/update-pipeline.ts
 *
 * Outbound pipeline: renders psref:/// URIs in upstream update text blocks
 * before forwarding to the ACP client.
 *
 * Only text blocks are scanned — other block types carry no inline references.
 * Returns new objects; originals are never mutated.
 * Throws if the resolver fails — no swallowing.
 */

import type * as acp from "@agentclientprotocol/sdk";
import { parseReferences } from "@pineseed/spec/psref";
import { renderReferences } from "@pineseed/cli/src/reference-renderer";
import type { ReferenceResolver } from "@pineseed/cli/src/reference-renderer";
import type { MimeResolver } from "./types";

// ─── Config ───────────────────────────────────────────────────────────────────

export interface UpdatePipelineConfig {
  /** Resolves a psref:/// Reference to its public display URL. */
  resolver: ReferenceResolver;
  /** Resolves a file extension (no dot) to its MIME type. */
  mimeResolver: MimeResolver;
}

// ─── UpdatePipeline ───────────────────────────────────────────────────────────

export class UpdatePipeline {
  readonly #config: UpdatePipelineConfig;

  constructor(config: UpdatePipelineConfig) {
    this.#config = config;
  }

  /**
   * Render all psref:/// URIs found in text content blocks of an update.
   * Returns a new update with rendered text; returns the original reference
   * unchanged when no text blocks or no psref URIs are present.
   */
  async render(
    update: acp.SessionNotification["update"],
  ): Promise<acp.SessionNotification["update"]> {
    if (!update) return update;

    // content may be an array (normal updates) or a single object (load session replay).
    // Preserve the original shape in the returned update.
    const rawContent: unknown = (update as any).content;
    if (!rawContent) return update;

    const isSingle = !Array.isArray(rawContent);
    const content: acp.ContentBlock[] = isSingle
      ? [rawContent as acp.ContentBlock]
      : (rawContent as acp.ContentBlock[]);
    if (content.length === 0) return update;

    let mutated = false;
    const renderedBlocks: acp.ContentBlock[] = [];

    for (const block of content) {
      if (block.type !== "text") {
        renderedBlocks.push(block);
        continue;
      }

      const textBlock = block as acp.TextContent;
      const text = textBlock.text ?? "";
      const refs = parseReferences(text, this.#config.mimeResolver);

      if (refs.length === 0) {
        renderedBlocks.push(block);
        continue;
      }

      const rendered = await renderReferences(
        text,
        refs,
        this.#config.resolver,
      );
      renderedBlocks.push({ ...textBlock, text: rendered } as acp.ContentBlock);
      mutated = true;
    }

    if (!mutated) return update;

    return {
      ...(update as object),
      content: isSingle ? renderedBlocks[0] : renderedBlocks,
    } as acp.SessionNotification["update"];
  }
}
